<template>
    <modal-component
        ref="lightModal"
        :title="modalTitle"
        class-name="e-lighting-light-modal"
        @close="handleHidden"
    >
        <template #default>
            <div class="light-modal-body">
                <div class="light-modal-form">
                    <div class="d-flex flex-wrap light-inline-row">
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label>Test Switch Number</label>
                            <input
                                ref="testSwitchInput"
                                v-model="localLight.sw_no"
                                type="text"
                                class="form-control"
                            >
                        </div>
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label>Light Number</label>
                            <input
                                v-model="localLight.light_no"
                                type="text"
                                class="form-control"
                            >
                        </div>
                    </div>

                    <div class="d-flex flex-wrap light-inline-row light-type-image-row">
                        <div class="light-inline-group light-inline-group--half light-type-column">
                            <div class="form-group light-sub-group">
                                <label>Type</label>
                                <select
                                    v-model="localLight.type"
                                    class="form-control"
                                    @change="typeChanged"
                                >
                                    <option value="">
                                        Select type
                                    </option>
                                    <option
                                        v-for="type in presetLightTypes"
                                        :key="type"
                                        :value="type"
                                    >
                                        {{ type }}
                                    </option>
                                    <option value="Other">
                                        Other
                                    </option>
                                </select>
                                <div
                                    v-if="showValidation && !localLight.type"
                                    class="modal-error"
                                >
                                    Light type is required.
                                </div>
                            </div>

                            <div
                                v-if="isOtherType"
                                class="form-group light-sub-group"
                            >
                                <input
                                    v-model="localLight.other_type_label"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter type here..."
                                >
                                <div
                                    v-if="showValidation && !localLight.other_type_label"
                                    class="modal-error"
                                >
                                    Please enter the custom light type.
                                </div>
                            </div>

                            <div
                                v-if="isOtherType"
                                class="form-group light-sub-group mb-0"
                            >
                                <div class="other-photo-actions">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-danger"
                                        :disabled="!localLight.photo_url"
                                        @click.prevent="deletePhoto"
                                    >
                                        Delete Photo
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-primary"
                                        @click.prevent="$refs.fileInput.click()"
                                    >
                                        Upload Photo
                                    </button>
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        accept="image/*"
                                        class="d-none"
                                        @change="handleFileUpload"
                                    >
                                </div>
                                <div
                                    v-if="showValidation && !localLight.photo_url"
                                    class="modal-error"
                                >
                                    You must upload a photo for this light type.
                                </div>
                            </div>
                        </div>

                        <div class="light-inline-group light-inline-group--half light-preview-column">
                            <div class="light-modal-preview light-modal-preview--inline light-modal-preview--stacked">
                                <img
                                    v-if="previewImageUrl"
                                    :src="previewImageUrl"
                                    class="light-preview-image"
                                    alt="Light preview"
                                >
                                <div
                                    v-else
                                    class="light-preview-placeholder"
                                >
                                    Please upload a photo for this light
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex flex-wrap light-inline-row">
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label>Location of Light</label>
                            <input
                                v-model="localLight.light_location"
                                type="text"
                                class="form-control"
                            >
                            <div
                                v-if="showValidation && !localLight.light_location"
                                class="modal-error"
                            >
                                Location of Light is required.
                            </div>
                        </div>
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label>Location of Switch</label>
                            <input
                                v-model="localLight.switch_location"
                                type="text"
                                class="form-control"
                            >
                        </div>
                    </div>

                    <div class="d-flex flex-wrap light-inline-row">
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label class="d-block">Maintained</label>
                            <label class="checkbox-label">
                                Tick here if this light is maintained:
                                <input
                                    v-model="localLight.maintained"
                                    type="checkbox"
                                >
                            </label>
                        </div>
                        <div
                            v-if="showHigh"
                            class="form-group light-inline-group light-inline-group--half"
                        >
                            <label class="d-block">High</label>
                            <label class="checkbox-label">
                                Tick here if this light is high:
                                <input
                                    v-model="localLight.high"
                                    type="checkbox"
                                >
                            </label>
                        </div>
                    </div>

                    <div
                        v-if="showNotAccessible"
                        class="d-flex flex-wrap light-inline-row"
                    >
                        <div class="form-group light-inline-group light-inline-group--half">
                            <label class="d-block">Not Accessible</label>
                            <label class="checkbox-label">
                                Tick here if this light is not accessible:
                                <input
                                    v-model="localLight.not_accessible"
                                    type="checkbox"
                                    @change="notAccessibleChanged"
                                >
                            </label>
                        </div>
                    </div>

                    <div
                        v-if="showTested"
                        class="d-flex flex-wrap light-inline-row"
                    >
                        <div class="form-group light-inline-group light-inline-group--full">
                            <label class="d-block">Tested</label>
                            <label class="button-radio mr-2">
                                <input
                                    v-model="localLight.test_result"
                                    type="radio"
                                    value="passed"
                                    @click="toggleResult('passed')"
                                >
                                <span class="button-radio-text">Pass</span>
                            </label>
                            <label class="button-radio">
                                <input
                                    v-model="localLight.test_result"
                                    type="radio"
                                    value="failed"
                                    @click="toggleResult('failed')"
                                >
                                <span class="button-radio-text button-radio-text--failed">Fail</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="w-100 d-flex justify-content-between margin-top-2">
                <button
                    v-if="mode === 'edit'"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click.prevent="requestDelete"
                >
                    Delete
                </button>
                <div v-else />
                <div class="d-flex">
                    <button
                        type="button"
                        class="btn btn-secondary mr-2"
                        @click.prevent="close"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click.prevent="save"
                    >
                        OK
                    </button>
                </div>
            </div>
        </template>
    </modal-component>
</template>

<script>
import UploadService from "../../../services/UploadService";
import ModalComponent from "../../modals/ModalComponent.vue";
import {
    PRESET_LIGHT_TYPES,
    buildPresetTypeImages,
} from "../../../include/ElightingLights";

export default {
    name: "ELightingLightModal",
    components: {
        ModalComponent,
    },
    props: {
        showHigh: {
            type: Boolean,
            default: true,
        },
        showNotAccessible: {
            type: Boolean,
            default: true,
        },
        showTested: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            mode: "add",
            editIndex: null,
            localLight: {},
            showValidation: false,
            originalLightSnapshot: null,
        };
    },
    computed: {
        presetLightTypes() {
            return PRESET_LIGHT_TYPES;
        },
        presetTypeImages() {
            return buildPresetTypeImages();
        },
        modalTitle() {
            return this.mode === "edit" ? "Edit Light" : "Add Light";
        },
        isOtherType() {
            return this.localLight.type === "Other";
        },
        previewImageUrl() {
            if (this.isOtherType) {
                return this.localLight.photo_url;
            }
            if (!this.localLight.type) {
                return "";
            }
            return this.presetTypeImages[this.localLight.type] || "";
        },
    },
    methods: {
        open(config) {
            this.mode = config.mode;
            this.editIndex = config.index;
            this.localLight = this.createLightDraft(config.light);
            this.originalLightSnapshot = this.createLightSnapshot(this.localLight);
            this.showValidation = false;
            this.$refs.lightModal.open();
            this.$nextTick(() => {
                if (this.$refs.testSwitchInput) {
                    this.$refs.testSwitchInput.focus();
                }
            });
        },
        close() {
            this.$refs.lightModal.close();
        },
        handleHidden() {
            this.showValidation = false;
        },
        createLightDraft(light) {
            const draft = { ...light };
            if (typeof draft.type === "undefined") {
                draft.type = "";
            }
            if (typeof draft.other_type_label === "undefined") {
                draft.other_type_label = "";
            }
            if (typeof draft.photo_url === "undefined") {
                draft.photo_url = "";
            }
            draft._is_existing_light = !!draft._is_existing_light;
            draft.maintained = !!draft.maintained;
            draft.high = !!draft.high;
            draft.not_accessible = !!draft.not_accessible;
            if (!draft.test_result) {
                draft.test_result = null;
            }
            return draft;
        },
        createLightSnapshot(light) {
            return JSON.stringify({
                sw_no: light.sw_no || "",
                light_no: light.light_no || "",
                type: light.type || "",
                other_type_label: light.other_type_label || "",
                photo_url: light.photo_url || "",
                light_location: light.light_location || "",
                switch_location: light.switch_location || "",
                maintained: !!light.maintained,
                high: !!light.high,
                not_accessible: !!light.not_accessible,
                test_result: light.test_result || null,
            });
        },
        hasLightChangedInModal() {
            if (!this.originalLightSnapshot) {
                return false;
            }
            return this.createLightSnapshot(this.localLight) !== this.originalLightSnapshot;
        },
        toggleResult(result) {
            if (this.localLight.test_result === result) {
                this.localLight.test_result = null;
            }
        },
        notAccessibleChanged() {
            if (this.localLight.not_accessible) {
                this.localLight.tested = false;
                this.localLight.test_result = null;
            }
        },
        typeChanged() {
            if (!this.isOtherType) {
                this.localLight.other_type_label = "";
                this.localLight.photo_url = "";
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) {
                return;
            }
            UploadService.upload(file, 500).then((res) => {
                this.localLight.photo_url = res.data.full_url;
            });
        },
        deletePhoto() {
            const originalPhoto = this.localLight.photo_url;
            if (!originalPhoto) {
                return;
            }
            this.localLight.photo_url = "";
            UploadService.delete(originalPhoto).catch(() => {
                this.localLight.photo_url = originalPhoto;
                window.alert("Failed to delete photo");
                this.$forceUpdate();
            });
            this.$forceUpdate();
        },
        validateLight() {
            if (!this.localLight.type) {
                return false;
            }
            if (!this.localLight.light_location) {
                return false;
            }
            if (!this.isOtherType) {
                return true;
            }
            if (this.localLight._is_existing_light) {
                return true;
            }
            if (!this.localLight.other_type_label) {
                return false;
            }
            return !!this.localLight.photo_url;
        },
        save() {
            if (
                this.mode === "edit" &&
                this.localLight._is_existing_light &&
                this.hasLightChangedInModal()
            ) {
                this.localLight._is_existing_light = false;
            }
            this.showValidation = true;
            if (!this.validateLight()) {
                return;
            }
            const payload = {
                light: { ...this.localLight },
                mode: this.mode,
                index: this.editIndex,
            };
            this.$emit("save", payload);
            this.close();
        },
        requestDelete() {
            this.$emit("request-delete", this.editIndex);
            this.close();
        },
    },
};
</script>

<style scoped lang="scss">
.light-modal-body {
    display: block;
}

.light-modal-form {
    flex: 1;
}

.light-inline-row {
    margin-bottom: 0.5rem;
    margin-left: -0.35rem;
    margin-right: -0.35rem;
}

.light-inline-group {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    margin-bottom: 1.2rem;
}

.light-inline-group--half {
    flex: 1 1 50%;
    min-width: 220px;
}

.light-inline-group--full {
    flex: 1 1 100%;
}

.light-type-image-row {
    align-items: stretch;
}

.light-type-column {
    display: flex;
    flex-direction: column;
}

.light-sub-group {
    margin-bottom: 0.95rem;
}

.light-preview-column {
    display: flex;
}

.light-modal-preview {
    width: 100%;
    min-height: 120px;
    border: 1px solid #e5e5e5;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
}

.light-modal-preview--inline {
    max-width: 260px;
}

.light-modal-preview--stacked {
    width: 100%;
    max-width: none;
    min-height: 150px;
    height: 100%;
}

.light-preview-image {
    max-width: 100%;
    max-height: 180px;
}

.light-preview-placeholder {
    color: #c3c3c3;
    transform: rotate(-35deg);
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: normal;
}

.other-photo-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.modal-error {
    color: #b84f37;
    margin-top: 0.3rem;
    font-size: 0.85rem;
}

.button-radio {
    display: inline-block;
    margin: 0 1px;

    input {
        display: none;
    }

    &-text {
        display: block;
        padding: 5px 12px;
        background: #eeeeee;
        border-radius: 3px;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: #a8a8a8;
        }
    }

    input[type="radio"]:checked + .button-radio-text {
        background: #89ca70;
        color: #ffffff;
    }

    input[type="radio"]:checked + .button-radio-text--failed {
        background: #dc3545;
        color: #ffffff;
    }
}

@media (max-width: 768px) {
    .light-inline-group--half {
        flex: 1 1 100%;
        min-width: 0;
    }

    .light-modal-preview--inline {
        max-width: none;
    }

    .light-preview-column {
        display: block;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/forms/parts/ELightingLightModal.vue